import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { brand } from '@/brand';
import { LanguageSwitcher } from '@/features/language';
import { Logo } from '@/shared/ui/Logo';
import { AuthHeader } from './AuthHeader';
import { Picture } from './Picture';

// Раскладка гостевых страниц: вход, регистрация, сброс и смена пароля, 2FA,
// подтверждение почты, вход по токену. Раньше каждая страница повторяла одну и
// ту же обёртку (фон, колонка с шапкой, иллюстрация) — восемь копий разметки.
// Вид задаёт бренд (brand.layout.auth), форма внутри общая:
//
// 'split' — прежняя разметка 1:1: форма слева на фоне темы, шапка с вопросом и
//           ссылкой («Нет аккаунта? Регистрация») над ней, иллюстрация справа.
// 'card'  — макет telecom: картинка темы на весь экран (--auth-bg-image), язык
//           в углу, логотип, белая карточка с заголовком по центру; вопрос со
//           ссылкой — под формой. Выбор языка здесь есть, потому что лендинга,
//           где его выбирают у serious, у этого бренда нет.

type AuthLayoutProps = {
  title: string;
  // Вопрос со ссылкой: «Нет аккаунта?» + «Регистрация».
  questionText?: string;
  linkTo?: string;
  linkText?: string;
  // Пояснение под заголовком (сброс пароля).
  hint?: ReactNode;
  // 'card': карточка шире — поля регистрации стоят в две колонки.
  wide?: boolean;
  // 'split': доп. классы колонки формы (отступ снизу у длинной регистрации).
  columnClassName?: string;
  children?: ReactNode;
};

const SplitLayout = ({ title, questionText, linkTo, linkText, hint, columnClassName, children }: AuthLayoutProps) => (
  <div className="flex min-h-screen flex-col items-center bg-surface-auth-page px-4 py-5 xl:h-full xl:flex-row xl:items-stretch xl:justify-between xl:gap-[105px] xl:py-[16px]">
    <div
      className={cn(
        'flex w-full flex-col sm:mt-[60px] sm:max-w-[480px] sm:items-center xl:ml-[7.6%] xl:mt-[64px] xl:w-1/2 xl:items-start',
        columnClassName,
      )}
    >
      <AuthHeader title={title} questionText={questionText} linkTo={linkTo} linkText={linkText} />
      {hint && <p className="mt-6 text-content-auth-secondary">{hint}</p>}
      {children}
    </div>

    <div className="w-[50.5%]">
      <Picture />
    </div>
  </div>
);

const CardLayout = ({ title, questionText, linkTo, linkText, hint, wide, children }: AuthLayoutProps) => {
  const { t } = useTranslation('auth');

  return (
    <div
      className="relative flex min-h-screen flex-col items-center bg-surface-auth-page bg-cover bg-center px-4 pb-6 pt-4 sm:px-16 sm:pb-10 sm:pt-14 lg:pt-12"
      style={{ backgroundImage: 'var(--auth-bg-image)' }}
    >
      <div className="mb-4 flex w-full justify-end sm:absolute sm:right-6 sm:top-6 sm:mb-0 sm:w-auto">
        <LanguageSwitcher tone="deep" />
      </div>

      <Link to="/" aria-label={t('toHome')}>
        <Logo min={false} size="hero" />
      </Link>

      <div
        className={cn(
          'mt-7 w-full rounded-3xl bg-surface-auth-panel px-5 py-8 shadow-[0_22px_48px_rgb(40_0_0/0.24)] sm:mt-12 sm:rounded-[32px] sm:px-12 sm:py-11 lg:mt-10',
          wide ? 'max-w-[600px]' : 'max-w-[480px]',
        )}
      >
        <h1 className="text-center text-[26px] font-extrabold leading-8 text-content-auth-heading sm:text-[30px] sm:leading-9">
          {title}
        </h1>
        {hint && <p className="mt-2.5 text-pretty text-center text-sm text-content-auth-secondary">{hint}</p>}
        {children}
        {(questionText || (linkTo && linkText)) && (
          <div className="mt-[18px] text-center text-[13px] text-content-auth-secondary">
            {questionText}{' '}
            {linkTo && linkText && (
              <Link to={linkTo} className="font-bold text-content-auth-link hover:text-content-auth-link-hover">
                {linkText}
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const AuthLayout = (props: AuthLayoutProps) =>
  brand.layout.auth === 'card' ? <CardLayout {...props} /> : <SplitLayout {...props} />;
